// Translate `Field Picture String` into `Inputmasks` format
// [Field Picture String]: https://service.financialsynergy.com.au/display/SU/Field+Picture+String
// [Inputmask]: https://github.com/RobinHerbots/Inputmask
// See InputMaskValidator.java

const OPTIONAL_HASH_PATTERN = new RegExp(/#+(?!\{\d+,\d+\})/, 'g');
const OPTIONAL_NINE_PATTERN = new RegExp(/9+(?!\{\d+,\d+\})/, 'g');

const MIXED_OPTIONAL_HASH_PATTERN = new RegExp(/#{2,}(?=\{\d+,\d+\})/);

const MIXED_OPTIONAL_NINE_PATTERN = new RegExp(/9{2,}(?=\{\d+,\d+\})/);

const DECIMAL_FORMAT_HASH_PATTERN = new RegExp(
	/^#+@#+|#\{\d+,\d+\}\[?@#\{\d+,\d+\}\]?$/,
);
const DECIMAL_FORMAT_NINE_PATTERN = new RegExp(
	/^9+@9+|9\{\d+,\d+\}\[?@9\{\d+,\d+\}\]?$/,
);
const INTEGER_FORMAT_HASH_PATTERN = new RegExp(/^#+|#\{\d+,\d+\}$/);
const INTEGER_FORMAT_NINE_PATTERN = new RegExp(/^9+|9\{\d+,\d+\}$/);

const DYNAMIC_INPUTMASK_PATTERN = new RegExp(/\{\d+,\d+\}/, 'g');
const DYNAMIC_INPUTMASK_RESERVE = 'OPTIONAL_INPUTMASK_RESERVE';

function validateInputmaskFormat(format: string): boolean {
	if (
		MIXED_OPTIONAL_HASH_PATTERN.test(format) ||
		MIXED_OPTIONAL_NINE_PATTERN.test(format)
	) {
		return false;
	}
	const decimalDotCount = (format.match('@') || []).length;
	if (decimalDotCount > 1) {
		return false;
	} else if (decimalDotCount === 1) {
		return (
			DECIMAL_FORMAT_HASH_PATTERN.test(format) ||
			DECIMAL_FORMAT_NINE_PATTERN.test(format)
		);
	}
	return true;
}

// WEBUI-1232: convert field picture string to inputmask format
// e.g, ###@#### => #{0,3}[@#{1,4}]
export function convertInputMask(format: string): string {
	if (!validateInputmaskFormat(format)) {
		throw new Error('Invalid inputmask format');
	}

	if ((format.match('@') || []).length === 1) {
		// if input mask format is /^#+@#+$/ or /^9+@9+$/, fractional-part is optional input
		// once dot is input, fractional-part should be at least one digit
		if (/^#+@#+|9+@9+$/.test(format)) {
			format = format.replace(/@#+|@9+$/, (match) => `[${match}]`);
		}
	}

	// Put all dynamic pattern into an array and replace them as {n}
	// e.g 9{1,9}-9{1,19}-9{1,49} has three dynamic input mask pattern. Reserve them into array
	// ['{1,9}', '{1,19}', '{1,49}'] and the input mask format becomes 9{0}-9{1}-9{2}
	// if it is nth pattern and n is a number contains '9', skip and use next index
	// Indeed, I don't think we would make an input mask format that has 9 dynamic pattern. Who is such trouble maker.
	const reserveArray = [];
	function dynamicPatternReserver(match) {
		while (`${reserveArray.length}`.includes('9')) {
			reserveArray.push(undefined);
		}
		reserveArray.push(match);
		return `{${reserveArray.length - 1}}`;
	}
	format = format.replace(DYNAMIC_INPUTMASK_PATTERN, dynamicPatternReserver);

	// We already handle a special case which 9 inside the dynamic pattern
	// note that dynamic inputmask format {n,m} contains no space in {n,m}
	format = format.replace(OPTIONAL_HASH_PATTERN, (match) =>
		match.length === 1 ? '#' : `#{1,${match.length}}`,
	);
	format = format.replace(OPTIONAL_NINE_PATTERN, (match) =>
		match.length === 1 ? '9' : `9{1,${match.length}}`,
	);

	// Recover input mask format from reserve array
	reserveArray.forEach(
		(val, index) => val && (format = format.replace(`{${index}}`, val)),
	);

	return format;
}

/**
 * Only used by number field. If the number field has inputmask, we still want to apply inputmask alias
 * keep mask number in aligned with those without inputmask. But we should turn off auto group because it
 * is buggy and stop input working correctly.
 */
export function calculateNumericAliasFromInputmask(
	format: string,
): string | undefined {
	if (
		DECIMAL_FORMAT_HASH_PATTERN.test(format) ||
		DECIMAL_FORMAT_NINE_PATTERN.test(format)
	) {
		return 'decimalWithoutAutoGroup';
	}
	if (
		INTEGER_FORMAT_HASH_PATTERN.test(format) ||
		INTEGER_FORMAT_NINE_PATTERN.test(format)
	) {
		return 'integerWithoutAutoGroup';
	}
	return undefined;
}

/**
 * Only used by number fields with a valid decimal format inputmask. Returns the number of
 * integer digits for the given mask, i.e. the number of digits to the left of the decimal place.
 */
export function calculateIntegerDigitsFromInputmask(format: string): number {
	if (
		!DECIMAL_FORMAT_HASH_PATTERN.test(format) &&
		!DECIMAL_FORMAT_NINE_PATTERN.test(format)
	) {
		throw new Error('Invalid decimal format inputmask');
	}
	return format.indexOf('@');
}
